import React, { useState, useEffect } from "react";
import {
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  VStack,
  HStack,
  FormErrorMessage,
  useToast,
  Spinner,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Divider,
} from "@chakra-ui/react";
import ReactSelect from "react-select";
import { BsPlus, BsTrash } from "react-icons/bs";
import vendorApi from "../services/vendorApi";
import vendorPOApi from "../services/vendorPOApi";

const createEmptyLineItem = () => ({
  id: Date.now() + Math.random(),
  requestLineItemId: "",
  itemName: "",
  description: "",
  quantity: 1,
  unit: "pcs",
  unitPrice: "",
  totalPrice: 0,
  notes: "",
});

const randomInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const randomCode = () =>
  `${Date.now().toString().slice(-6)}${randomInt(100, 999)}`;

const AddVendorPOModal = ({
  isOpen,
  onClose,
  requestId,
  onSuccess,
  requestLineItems = [],
}) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [formData, setFormData] = useState({
    vendorId: "",
    poNumber: "",
    currency: "INR",
    poDocumentUrl: "",
    notes: "",
  });
  const [proposalLineItems, setProposalLineItems] = useState([]);
  const [errors, setErrors] = useState({});

  const vendorOptions = vendors.map((vendor) => ({
    value: vendor._id,
    label: `${vendor.name} (${vendor.email})`,
  }));

  const initializeProposalLineItems = () => {
    if (requestLineItems.length > 0) {
      return requestLineItems.map((item, index) => ({
        id: item._id || `${Date.now()}-${index}`,
        requestLineItemId: item._id || "",
        itemName: item.itemName || "",
        description: item.description || "",
        quantity: item.quantity || 1,
        unit: item.unit || "pcs",
        unitPrice: "",
        totalPrice: 0,
        notes: "",
      }));
    }

    return [createEmptyLineItem()];
  };

  useEffect(() => {
    if (isOpen) {
      fetchActiveVendors();
      // Reset form when modal opens
      setFormData({
        vendorId: "",
        poNumber: "",
        currency: "INR",
        poDocumentUrl: "",
        notes: "",
      });
      setProposalLineItems(initializeProposalLineItems());
      setErrors({});
    }
  }, [isOpen, requestLineItems]);

  const fetchActiveVendors = async () => {
    try {
      setVendorsLoading(true);
      const response = await vendorApi.getActiveVendors();
      setVendors(response?.data || []);
    } catch (error) {
      console.error("❌ Error fetching vendors:", error);
      toast({
        title: "Error fetching vendors",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setVendorsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const updateLineItem = (id, field, value) => {
    setProposalLineItems((currentItems) =>
      currentItems.map((item) => {
        if (item.id !== id) {
          return item;
        }

        const updatedItem = { ...item, [field]: value };
        const quantity = Number(updatedItem.quantity || 0);
        const unitPrice = Number(updatedItem.unitPrice || 0);
        updatedItem.totalPrice = quantity * unitPrice;
        return updatedItem;
      }),
    );

    if (errors.lineItems) {
      setErrors({ ...errors, lineItems: null });
    }
  };

  const addLineItem = () => {
    setProposalLineItems((currentItems) => [
      ...currentItems,
      createEmptyLineItem(),
    ]);
  };

  const removeLineItem = (id) => {
    setProposalLineItems((currentItems) => {
      if (currentItems.length === 1) {
        return currentItems;
      }
      return currentItems.filter((item) => item.id !== id);
    });
  };

  const calculateQuotedAmount = () =>
    proposalLineItems.reduce(
      (sum, item) => sum + Number(item.totalPrice || 0),
      0,
    );

  const handleQuickFillTestData = () => {
    const randomVendor = vendors[randomInt(0, Math.max(vendors.length - 1, 0))];
    const defaultItems = [
      "Projector",
      "Installation",
      "HDMI Cable",
      "Screen",
      "Delivery",
      "Warranty",
      "Training",
      "Mounting Kit",
    ];

    setFormData((currentData) => ({
      ...currentData,
      vendorId: currentData.vendorId || randomVendor?._id || "",
      poNumber: currentData.poNumber || `QT-TEST-${randomCode()}`,
      poDocumentUrl:
        currentData.poDocumentUrl ||
        `https://example.com/quotation-test-${randomCode()}.pdf`,
      notes:
        currentData.notes ||
        `Auto-generated test quotation data (${new Date().toLocaleString()})`,
    }));

    setProposalLineItems(() => {
      // Force a multi-line test case dataset for quick QA scenarios.
      const minimumRows = Math.max(4, requestLineItems.length || 0);
      const baseItems =
        requestLineItems.length > 0
          ? requestLineItems.map((lineItem, index) => ({
              id: `${Date.now()}-${index}-${randomInt(10, 99)}`,
              requestLineItemId: lineItem._id || "",
              itemName:
                lineItem.itemName || defaultItems[index % defaultItems.length],
              description: lineItem.description || "",
              quantity: Number(lineItem.quantity || randomInt(1, 3)),
              unit: lineItem.unit || "pcs",
              unitPrice: randomInt(500, 25000),
              totalPrice: 0,
              notes: "",
            }))
          : [];

      while (baseItems.length < minimumRows) {
        const index = baseItems.length;
        baseItems.push({
          ...createEmptyLineItem(),
          id: `${Date.now()}-${index}-${randomInt(10, 99)}`,
          itemName: defaultItems[index % defaultItems.length],
          description: `Auto test data for ${defaultItems[index % defaultItems.length]}`,
          quantity: randomInt(1, 4),
          unit: "pcs",
          unitPrice: randomInt(500, 25000),
        });
      }

      return baseItems.map((item, index) => {
        const quantity = Number(item.quantity || randomInt(1, 4));
        const unitPrice = Number(item.unitPrice || randomInt(500, 25000));

        return {
          ...item,
          itemName: item.itemName || defaultItems[index % defaultItems.length],
          description:
            item.description ||
            `Auto test data for ${defaultItems[index % defaultItems.length]}`,
          quantity,
          unit: item.unit || "pcs",
          unitPrice,
          totalPrice: quantity * unitPrice,
        };
      });
    });

    setErrors({});
    toast({
      title: "Test data filled",
      description: "Random quotation values added for quick testing",
      status: "info",
      duration: 1500,
      isClosable: true,
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.vendorId) {
      newErrors.vendorId = "Please select a vendor";
    }

    if (!formData.poNumber || formData.poNumber.trim().length < 3) {
      newErrors.poNumber = "Quotation Number must be at least 3 characters";
    }

    if (proposalLineItems.length === 0) {
      newErrors.lineItems = "At least one line item is required";
    }

    const invalidLineItems = proposalLineItems.filter(
      (item) =>
        !item.itemName?.trim() ||
        !item.quantity ||
        Number(item.quantity) <= 0 ||
        item.unitPrice === "" ||
        Number(item.unitPrice) < 0,
    );
    if (invalidLineItems.length > 0) {
      newErrors.lineItems =
        "Each line item must have item name, quantity, and unit price";
    }

    if (calculateQuotedAmount() <= 0) {
      newErrors.lineItems =
        "At least one line item must have a quoted amount greater than 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const proposalData = {
        ...formData,
        lineItems: proposalLineItems.map(({ id, ...item }) => ({
          ...item,
          quantity: Number(item.quantity || 0),
          unitPrice: Number(item.unitPrice || 0),
          totalPrice: Number(item.totalPrice || 0),
        })),
      };

      await vendorPOApi.addVendorPOProposal(requestId, proposalData);

      toast({
        title: "Vendor PO Proposal added successfully",
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      onSuccess();
    } catch (error) {
      console.error("❌ Error adding vendor PO proposal:", error);
      toast({
        title: "Error adding proposal",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add Vendor PO Proposal</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit}>
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <HStack justify="flex-end">
                <Button
                  size="xs"
                  variant="outline"
                  onClick={handleQuickFillTestData}
                  isDisabled={vendorsLoading}
                >
                  Quick Fill Test Data
                </Button>
              </HStack>

              {/* Vendor Selection */}
              <FormControl isInvalid={errors.vendorId} isRequired>
                <FormLabel>Select Vendor</FormLabel>
                {vendorsLoading ? (
                  <Spinner size="sm" />
                ) : vendors.length === 0 ? (
                  <Text fontSize="sm" color="gray.500">
                    No active vendors available. Please add vendors first.
                  </Text>
                ) : (
                  <ReactSelect
                    placeholder="Search and select a vendor"
                    isClearable
                    options={vendorOptions}
                    value={
                      vendorOptions.find(
                        (option) => option.value === formData.vendorId,
                      ) || null
                    }
                    onChange={(selectedOption) =>
                      handleInputChange(
                        "vendorId",
                        selectedOption ? selectedOption.value : "",
                      )
                    }
                  />
                )}
                <FormErrorMessage>{errors.vendorId}</FormErrorMessage>
              </FormControl>

              {/* Quotation Number */}
              <FormControl isInvalid={errors.poNumber} isRequired>
                <FormLabel>Quotation Number</FormLabel>
                <Input
                  placeholder="e.g., QT-2026-001"
                  value={formData.poNumber}
                  onChange={(e) =>
                    handleInputChange("poNumber", e.target.value)
                  }
                />
                <FormErrorMessage>{errors.poNumber}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel>Currency</FormLabel>
                <Select
                  value={formData.currency}
                  onChange={(e) =>
                    handleInputChange("currency", e.target.value)
                  }
                >
                  <option value="INR">₹ INR</option>
                  <option value="USD">$ USD</option>
                  <option value="EUR">€ EUR</option>
                  <option value="GBP">£ GBP</option>
                </Select>
              </FormControl>

              <FormControl isInvalid={errors.lineItems} isRequired>
                <HStack justify="space-between" align="center">
                  <FormLabel mb={0}>Quotation Line Items</FormLabel>
                  <Button
                    leftIcon={<BsPlus />}
                    size="xs"
                    variant="outline"
                    onClick={addLineItem}
                  >
                    Add Row
                  </Button>
                </HStack>
                <Box borderWidth="1px" borderRadius="md" overflowX="auto">
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Item</Th>
                        <Th>Description</Th>
                        <Th isNumeric>Qty</Th>
                        <Th>Unit</Th>
                        <Th isNumeric>Unit Price</Th>
                        <Th isNumeric>Total</Th>
                        <Th></Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {proposalLineItems.map((item) => (
                        <Tr key={item.id}>
                          <Td minW="180px">
                            <Input
                              size="sm"
                              value={item.itemName}
                              onChange={(e) =>
                                updateLineItem(
                                  item.id,
                                  "itemName",
                                  e.target.value,
                                )
                              }
                              placeholder="Item name"
                              isReadOnly={Boolean(item.requestLineItemId)}
                            />
                          </Td>
                          <Td minW="180px">
                            <Input
                              size="sm"
                              value={item.description}
                              onChange={(e) =>
                                updateLineItem(
                                  item.id,
                                  "description",
                                  e.target.value,
                                )
                              }
                              placeholder="Description"
                            />
                          </Td>
                          <Td minW="90px">
                            <Input
                              size="sm"
                              type="number"
                              min={1}
                              value={item.quantity}
                              onChange={(e) =>
                                updateLineItem(
                                  item.id,
                                  "quantity",
                                  Number(e.target.value) || 1,
                                )
                              }
                            />
                          </Td>
                          <Td minW="90px">
                            <Input
                              size="sm"
                              value={item.unit}
                              onChange={(e) =>
                                updateLineItem(item.id, "unit", e.target.value)
                              }
                              placeholder="pcs"
                            />
                          </Td>
                          <Td minW="120px">
                            <Input
                              size="sm"
                              type="number"
                              min={0}
                              value={item.unitPrice}
                              onChange={(e) =>
                                updateLineItem(
                                  item.id,
                                  "unitPrice",
                                  e.target.value,
                                )
                              }
                              placeholder="0"
                            />
                          </Td>
                          <Td isNumeric fontWeight="semibold">
                            {formData.currency === "INR" ? "₹" : ""}
                            {Number(item.totalPrice || 0).toLocaleString()}
                          </Td>
                          <Td>
                            <IconButton
                              icon={<BsTrash />}
                              size="sm"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() => removeLineItem(item.id)}
                              isDisabled={proposalLineItems.length === 1}
                              aria-label="Remove quotation line item"
                            />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
                <HStack justify="space-between" mt={2}>
                  <Text fontSize="xs" color="gray.500">
                    Add quote price against each line item. Total quotation is
                    calculated automatically.
                  </Text>
                  <Text fontWeight="semibold">
                    Total: {formData.currency === "INR" ? "₹" : ""}
                    {calculateQuotedAmount().toLocaleString()}
                  </Text>
                </HStack>
                <FormErrorMessage>{errors.lineItems}</FormErrorMessage>
              </FormControl>

              <Divider />

              {/* PO Document URL */}
              <FormControl>
                <FormLabel>PO Document URL (Optional)</FormLabel>
                <Input
                  placeholder="https://example.com/documents/po.pdf"
                  value={formData.poDocumentUrl}
                  onChange={(e) =>
                    handleInputChange("poDocumentUrl", e.target.value)
                  }
                />
              </FormControl>

              {/* Notes */}
              <FormControl>
                <FormLabel>Notes (Optional)</FormLabel>
                <Textarea
                  placeholder="Payment terms, delivery timeline, special conditions..."
                  value={formData.notes}
                  onChange={(e) => handleInputChange("notes", e.target.value)}
                  rows={3}
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              type="submit"
              isLoading={loading}
              isDisabled={vendors.length === 0}
            >
              Add Proposal
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default AddVendorPOModal;
